<template>
    <Modal id="kitchen-orders-modal"
           data-component="KitchenOrdersModal"
           title="KITCHEN_MODAL.RESTORE_MODAL_TITLE"
           icon="fa-cloud-arrow-down"
           :cancel="cancel">
        <PageLoadingSpinner v-if="loading"></PageLoadingSpinner>
        <template v-else>
            <div class="mb-1 btn-group btn-group-justified">
                <label class="btn btn-default"
                       :class="{'active': !showAll}"
                       @click.prevent="setShowAll(false)">
                    {{ l('KITCHEN_MODAL.ORDERS_FOR_CURRENT_OPERATOR_BUTTON') }}
                </label>
                <label class="btn btn-default"
                       :class="{'active': showAll}"
                       @click.prevent="setShowAll(true)">
                    {{ l('KITCHEN_MODAL.ORDERS_FOR_ALL_OPERATORS_BUTTON') }}
                </label>
            </div>

            <CartOverview v-for="kitchenCart in sortedFilteredKitchenOrders"
                          :key="kitchenCart.thirdPartyRef"
                          :stashed-cart="kitchenCart"
                          :show-delete="true"
                          :show-restore="true"
                          @delete-clicked="deleteCartWithConfirmation(kitchenCart)"
                          @restore-clicked="restoreCart(kitchenCart)"
                          @preview-clicked="previewCart(kitchenCart)">
                <template v-slot:statuses>
                    <CartOverviewStatuses :cart="kitchenCart.currentCart" />
                </template>
            </CartOverview>
        </template>

        <template v-slot:footer>
            <div class="modl-footer">
                <SquareBtn size="lg"
                           class="cancel-btn btn-white"
                           @click="closeModal">
                    {{ l('CANCEL') }}
                </SquareBtn>
                <SquareBtn size="lg"
                           class="btn-white"
                           @click="viewTables">
                    {{ l('KITCHEN_MODAL.SELECT_TABLE') }}
                </SquareBtn>
            </div>
        </template>
    </Modal>
</template>

<script>
import CartOverview from '@/epos/vue/modals/common/CartOverview';
import CartOverviewStatuses from '@/epos/vue/modals/common/CartOverviewStatuses';
import FulFillmentState from '@/lib/Kitchen/FulfillmentState';
import Modal from '@/vue/components/Modals/Modal';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import SquareBtn from '@/vue/components/SquareBtn';
import { calculateFulfillmentState } from '@/lib/Kitchen/calculateFulfillmentState';
import { container } from '@/container';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { timeoutPromise } from '@/functions/functions';

export default {
    name: 'KitchenOrdersModal',

    components: {
        Modal,
        PageLoadingSpinner,
        CartOverview,
        CartOverviewStatuses,
        SquareBtn,
    },

    mixins: [
        ModalPropsMixin,
    ],

    data() {
        return {
            loading: true,
            showAll: false,
            kitchenCarts: [],
        };
    },

    computed: {
        /**
         * Filters to show only orders created by the current operator by default.
         * Sorts and displays oldest orders first.
         *
         * @return {Cart[]}
         */
        sortedFilteredKitchenOrders() {
            let carts = this.kitchenCarts;

            if (!this.showAll) {
                carts = this.kitchenCarts.filter((cart) => {
                    return cart.currentCart.originalOrder.sellersUserID === container.userService.getUserID();
                });
            }

            carts.sort((a, b) => b.currentCart.fulfillmentCreatedAt - a.currentCart.fulfillmentCreatedAt);

            return carts;
        },
    },

    created() {
        container.orderService.findActiveFulfillmentOrders()
            .then((kitchenCarts) => {
                this.loading = false;
                this.kitchenCarts = kitchenCarts;
            });
    },

    methods: {
        setShowAll(value) {
            this.showAll = value;
        },

        /**
         * @param {object} kitchenCart
         * @param {?Number} [childModalID] Identifier of a child modal calling back so that we can close if necessary.
         */
        deleteCartWithConfirmation(kitchenCart, childModalID) {
            let confirmationMessages = [];

            const fullyPaid = kitchenCart.currentCart.originalOrder.fullyPaid;

            const fulfillmentState = calculateFulfillmentState(
                kitchenCart.currentCart.restoredKitchenOrder.fulfillments
            );

            if (!fullyPaid && fulfillmentState === FulFillmentState.UNFULFILLED) {
                confirmationMessages.push('KITCHEN_MODAL.CONFIRM_DELETE_UNPAID_UNFULFILLED');
            } else if (!fullyPaid && fulfillmentState === FulFillmentState.FULFILLED) {
                confirmationMessages.push('KITCHEN_MODAL.CONFIRM_DELETE_UNPAID_FULFILLED');
            } else if (!fullyPaid && fulfillmentState === FulFillmentState.PART_FULFILLED) {
                confirmationMessages.push('KITCHEN_MODAL.CONFIRM_DELETE_PAID_PART_FULFILLED');
            } else if (fullyPaid && fulfillmentState === FulFillmentState.UNFULFILLED) {
                confirmationMessages.push('KITCHEN_MODAL.CONFIRM_DELETE_PAID_UNFULFILLED');
            } else if (fullyPaid && fulfillmentState === FulFillmentState.FULFILLED) {
                confirmationMessages.push('KITCHEN_MODAL.CONFIRM_DELETE_PAID_FULFILLED');
            } else if (fullyPaid && fulfillmentState === FulFillmentState.PART_FULFILLED) {
                confirmationMessages.push('KITCHEN_MODAL.CONFIRM_DELETE_PAID_PART_FULFILLED');
            }

            confirmationMessages.push('KITCHEN_MODAL.ARE_YOU_SURE');

            container.dialogService
                .confirm(
                    confirmationMessages,
                    {
                        cancelLabel: 'GO_BACK',
                        okLabel: 'KITCHEN_MODAL.DELETE_ORDER',
                        cancelColor: 'default',
                        okColor: 'danger',
                    }
                )
                .then((confirmed) => {
                    if (confirmed) {
                        return this.deleteCart(kitchenCart, childModalID);
                    }
                    // The user cancelled
                    return null;
                });
        },

        /**
         * To delete a kitchen order we restore the order to the cart and then set the qty of each line to zero. The
         * operator is then free to make any adjustments such as adding notes, return reasons, or adjusting the order to
         * control the amount (if any) that needs to be refunded.
         *
         * When an order is cancelled the kitchen app will update and 'Cancelled' kitchen receipts will also print.
         *
         * @param {StashedCart} kitchenCart
         * @param {?Number} [childModalID] Identifier of a child modal calling back so that we can close if necessary.
         */
        deleteCart(kitchenCart, childModalID) {
            kitchenCart.isDeleting = true;

            kitchenCart.currentCart.isEditingOrder = true;

            container.cartService.populateFromStash(kitchenCart)
                .then(() => {
                    // Having a tiny delay here makes it feel less jarring because if it all happens as soon as you
                    // click the button it's not clear where things went.
                    return timeoutPromise(200);
                }).then(async () => {
                    if (childModalID) {
                        container.vueModalFactory.close(childModalID);
                    }

                    await container.cartService.zeroQuantityAllLines();

                    this.resolve({
                        deleteConfirmed: true,
                    });
                })
                .finally(() => {
                    kitchenCart.isDeleting = false;
                });
        },

        /**
         * @param {StashedCart} kitchenCart
         * @param {?Number} [childModalID] Identifier of a child modal calling back so that we can close if necessary.
         */
        restoreCart(kitchenCart, childModalID) {
            kitchenCart.isRestoring = true;

            kitchenCart.currentCart.isEditingOrder = true;

            container.cartService.populateFromStash(kitchenCart)
                .then(() => {
                    // Having a tiny delay here makes it feel less jarring because if it all happens as soon as you
                    // click the button it's not clear where things went.
                    return timeoutPromise(200);
                }).then(() => {
                    if (childModalID) {
                        container.vueModalFactory.close(childModalID);
                    }
                    this.resolve({
                        restored: true,
                    });
                })
                .finally(() => {
                    kitchenCart.isRestoring = false;
                });
        },

        /**
         * @param {StashedCart} stashedCart
         */
        previewCart(stashedCart) {
            container.vueModalFactory.openAndIgnoreResult(
                MODAL_NAMES__EPOS.ORDER_PREVIEW,
                {
                    stashedCart,
                    parentModal: this,
                }
            );
        },

        viewTables() {
            container.vueModalFactory.open(MODAL_NAMES__EPOS.TABLE_PICKER)
                .then((res) => {
                    if (res && res.refSet) {
                        this.closeModal();
                    }
                });
        },

        closeModal() {
            container.vueModalFactory.closeAllOfType(MODAL_NAMES__EPOS.KITCHEN_ORDERS);
        },
    },
};
</script>
